import { type AuthorInfo, type SearchArticleMeta, toArticleKey } from "@serophin/nottldr-shared";
import { useReadArticles } from "@serophin/nottldr-shared/hooks";
import type { ComponentChildren } from "preact";
import { useMemo, useState } from "preact/hooks";
import { useIsSignedIn } from "../../platform/session";
import { ExploreButtons } from "../explore/ExploreButtons";
import { Toggle } from "../settings/Toggle";
import { ArticleCard } from "./ArticleCard";
import { useAudioPlaylist } from "./useAudioPlaylist";

export type ArticleListProps = {
  metas: SearchArticleMeta[];
  authors: Record<string, AuthorInfo>;
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  /** Changes when the list starts over (another filter), which stops what is playing. */
  resetKey?: string;
  showReadFilter?: boolean;
  emptyMessage?: ComponentChildren;
};

const SPINNER = <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />;

const Empty = ({ message }: { message?: ComponentChildren }) =>
  message ? (
    <div class="min-h-screen flex justify-center text-gray-900 dark:text-gray-100">
      <div class="max-w-md mx-auto px-6 py-12 text-center">
        <div class="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg p-8 mb-6 shadow-md">
          <p class="text-gray-600 dark:text-gray-400 mb-4">{message}</p>
        </div>
        <div class="mt-8">
          <ExploreButtons />
        </div>
      </div>
    </div>
  ) : (
    <div class="text-center py-5 text-neutral-500">No articles found</div>
  );

type ViewProps = ArticleListProps & { shown: SearchArticleMeta[]; toolbar?: ComponentChildren };

const Footer = ({ hasMore, onLoadMore, total, shown }: { hasMore: boolean; onLoadMore: () => void; total: number; shown: number }) => (
  <div class="text-center py-5">
    {!hasMore && total > 0 && <div class="text-gray-500 text-center my-5">No more articles</div>}
    {hasMore && (
      <button type="button" onClick={onLoadMore} class="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
        Load More
      </button>
    )}
    {shown < total && (
      <div class="text-center text-xs mt-1 pb-5 text-neutral-500">
        Showing {shown} of {total} articles
      </div>
    )}
  </div>
);

const ListView = ({ metas, shown, authors, hasMore, isLoading, onLoadMore, resetKey, emptyMessage, toolbar }: ViewProps) => {
  useAudioPlaylist(shown, resetKey);

  return (
    <div>
      {toolbar}
      {metas.length === 0 && !isLoading && <Empty message={emptyMessage} />}
      <div class="flex flex-col gap-6">
        {shown.map((meta, index) => (
          <ArticleCard key={`${meta.authorAlias}.${meta.id}`} meta={meta} author={authors[meta.authorAlias]} priority={index < 2} />
        ))}
      </div>
      {isLoading && <div class="flex justify-center items-center py-8">{SPINNER}</div>}
      {!isLoading && <Footer hasMore={hasMore} onLoadMore={onLoadMore} total={metas.length} shown={shown.length} />}
    </div>
  );
};

/** A list with the "Unread" switch: it reads the visitor's reads, so it only exists where the page asks for it. */
const ReadFilteredList = (props: ArticleListProps) => {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const { readKeys } = useReadArticles();
  const signedIn = useIsSignedIn();
  const { metas } = props;
  const shown = useMemo(
    () => (unreadOnly && signedIn ? metas.filter((meta) => !readKeys.has(toArticleKey(meta.authorAlias, meta.id))) : metas),
    [metas, unreadOnly, signedIn, readKeys],
  );

  const toolbar = props.metas.length > 0 && (
    <div class="mb-4 flex items-center gap-2 text-sm text-neutral-500">
      <Toggle checked={unreadOnly} onChange={setUnreadOnly} />
      <span class="text-sm text-neutral-500">{unreadOnly ? "Unread" : "All"}</span>
    </div>
  );

  return <ListView {...props} shown={shown} toolbar={toolbar} />;
};

export const ArticleList = (props: ArticleListProps) => (props.showReadFilter ? <ReadFilteredList {...props} /> : <ListView {...props} shown={props.metas} />);
